import type { PortalCustomization } from "@redotech/customer-portal-sdk/rpc/schema/portal-customization/get-portal-customization";
import { useTriggerLoadWithInput } from "@redotech/react-util/load";
import { useStyleProperty } from "@redotech/react-util/style";
import {
  applyPortalCustomizationTextOverlay,
  type PortalCustomizationTextOverlay,
} from "@redotech/redo-customer-portal-common/portal-customization-translation";
import { RpcClientContext } from "@redotech/redo-customer-portal-shared/contexts/rpc";
import { DEFAULT_LOCALE, useTranslation } from "@redotech/redo-i18n";
import { localizationEnabled } from "@redotech/redo-team-common/localization/settings";
import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  memo,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { SettingsContext } from "../contexts/settings";
import { PortalCustomizationStyleTag } from "./portal-customization-style-tag";
import { resolveCustomizationImage } from "./resolve-customization-field";

const EMPTY_TEXT_OVERLAY: PortalCustomizationTextOverlay = {};

export interface CustomizationContextValue {
  customization: PortalCustomization | null;
  preview: boolean;
}

export const CustomizationContext = createContext<CustomizationContextValue>({
  customization: null,
  preview: false,
});

export interface CustomizationApplicationContextValue {
  shouldApplyCustomization: boolean;
}

export const CustomizationApplicationContext =
  createContext<CustomizationApplicationContextValue>({
    shouldApplyCustomization: true,
  });

export const portalCustomizationDisabledRouteHandle = {
  portalCustomizationDisabled: true,
} as const;

export function routeDisablesPortalCustomization(handle: unknown): boolean {
  if (
    typeof handle !== "object" ||
    handle === null ||
    !("portalCustomizationDisabled" in handle)
  ) {
    return false;
  }
  return handle.portalCustomizationDisabled === true;
}

export const CustomizationApplicationProvider = memo(
  function CustomizationApplicationProvider({
    children,
    shouldApplyCustomization = true,
  }: {
    children: ReactNode | ReactNode[];
    shouldApplyCustomization?: boolean;
  }) {
    const value = useMemo<CustomizationApplicationContextValue>(
      () => ({ shouldApplyCustomization }),
      [shouldApplyCustomization],
    );

    return (
      <CustomizationApplicationContext.Provider value={value}>
        {children}
      </CustomizationApplicationContext.Provider>
    );
  },
);

export const CustomizationProvider = memo(function CustomizationProvider({
  children,
  preview = false,
  customizationOverride,
}: {
  children: ReactNode | ReactNode[];
  preview?: boolean;
  /** Optional in-memory customization that bypasses the RPC fetch. Used by
   * the merchant-app portal builder to show unsaved edits live. */
  customizationOverride?: PortalCustomization | null;
}) {
  const rpcClient = useContext(RpcClientContext);
  const settings = useContext(SettingsContext);
  const { i18n } = useTranslation();
  const language = i18n.language.split("-")[0] ?? i18n.language;
  const { shouldApplyCustomization } = useContext(
    CustomizationApplicationContext,
  );

  const [customizationLoad, refresh] = useTriggerLoadWithInput(
    async (_: void) => {
      if (!rpcClient) {
        return null;
      }
      const response = await rpcClient.getPortalCustomization({});
      return response;
    },
  );

  useEffect(() => {
    if (!shouldApplyCustomization) {
      return;
    }
    if (customizationOverride !== undefined) {
      return;
    }
    if (!rpcClient) {
      return;
    }
    refresh();
  }, [refresh, customizationOverride, rpcClient, shouldApplyCustomization]);

  const fetchedCustomization: PortalCustomization | null =
    !shouldApplyCustomization
      ? null
      : customizationOverride !== undefined
        ? (customizationOverride ?? null)
        : (customizationLoad.value?.customization ?? null);

  const shouldApplyTextOverlay =
    !!rpcClient &&
    shouldApplyCustomization &&
    customizationOverride === undefined &&
    localizationEnabled(settings?.localization) &&
    language !== DEFAULT_LOCALE;
  const overlayQuery = useQuery({
    queryKey: ["portalCustomizationTextOverlay", language],
    queryFn: async () => {
      if (!rpcClient) {
        return EMPTY_TEXT_OVERLAY;
      }
      const { overlay } = await rpcClient.getPortalCustomizationTextOverlay({
        language,
      });
      return overlay;
    },
    enabled: shouldApplyTextOverlay,
    staleTime: Infinity,
  });

  const customization: PortalCustomization | null = useMemo(() => {
    if (!fetchedCustomization || customizationOverride !== undefined) {
      return fetchedCustomization;
    }
    if (!shouldApplyTextOverlay) {
      return fetchedCustomization;
    }
    return applyPortalCustomizationTextOverlay(
      fetchedCustomization,
      overlayQuery.data ?? EMPTY_TEXT_OVERLAY,
    );
  }, [
    fetchedCustomization,
    customizationOverride,
    overlayQuery.data,
    shouldApplyTextOverlay,
  ]);
  // Apply the favicon to `<head>` as a side effect.
  const faviconUrl = useMemo(
    () =>
      resolveCustomizationImage(
        customization?.branding?.faviconUrl,
        settings?.brandKit?.images.faviconUrl ||
          settings?.brandKit?.images.logoUrl,
      ),
    [customization, settings],
  );
  useEffect(() => {
    if (preview || !shouldApplyCustomization || !faviconUrl) {
      return;
    }
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/x-icon";
    link.href = faviconUrl;
    document.head.append(link);
    return () => {
      link.remove();
    };
  }, [faviconUrl, preview, shouldApplyCustomization]);

  const shouldApplyThemeVariables = !preview && shouldApplyCustomization;
  const themeVariableValue = (value: string | null | undefined) =>
    shouldApplyThemeVariables ? (value ?? "inherit") : "inherit";

  // In preview mode and on order tracking pages, force every document-level CSS
  // var to `inherit` so the surrounding shell doesn't theme the current page.
  useStyleProperty(
    document.documentElement,
    "--redo-primary-color",
    themeVariableValue(settings?.theme.button_background_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-text-color",
    themeVariableValue(settings?.theme.button_text_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-primary-button-color",
    themeVariableValue(settings?.theme.button_background_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-accent-color",
    themeVariableValue(settings?.theme.accent_color),
  );
  useStyleProperty(
    document.documentElement,
    "--redo-accent-color-background",
    themeVariableValue(settings?.theme.accent_background_color),
  );
  useStyleProperty(
    document.documentElement,
    "--return-app-body-font-family",
    themeVariableValue(settings?.theme.body_font_family),
  );
  useStyleProperty(
    document.documentElement,
    "--return-app-title-font-family",
    themeVariableValue(settings?.theme.title_font_family),
  );

  const value = useMemo<CustomizationContextValue>(
    () => ({ customization, preview }),
    [customization, preview],
  );

  const hasLoadedOnce =
    !shouldApplyCustomization ||
    customizationOverride !== undefined ||
    customizationLoad.value !== undefined ||
    !!customizationLoad.error;

  if (!hasLoadedOnce) {
    return null;
  }

  return (
    <CustomizationContext.Provider value={value}>
      {!preview && shouldApplyCustomization && <PortalCustomizationStyleTag />}
      {children}
    </CustomizationContext.Provider>
  );
});
